import { DependencyList, EffectCallback, useEffect, useRef } from 'react';

const useNonInitialEffect = (effect: EffectCallback, deps?: DependencyList) => {
  const initialRender = useRef(true);

  useEffect(() => {
    let effectReturns: ReturnType<EffectCallback> = () => {};

    if (initialRender.current) {
      initialRender.current = false;
    } else {
      effectReturns = effect();
    }

    if (effectReturns && typeof effectReturns === 'function') {
      return effectReturns;
    }
  }, deps);
};

const toNumber = (
  value: string | Array<string> | boolean | number | null | undefined,
  defaultValue = 0,
) => {
  const num = new Number(value).valueOf();
  if (value === null || isNaN(num)) {
    return defaultValue;
  }
  return num;
};

function nameToParam(name: string, locale = 'id') {
  const andWord = locale === 'id' ? 'dan' : 'and';

  return name.replace(/\s/g, '-').replace(/\&/g, andWord).toLowerCase();
}

function paramToName(param: string, locale = 'id') {
  const andWord = locale === 'id' ? 'dan' : 'and';
  const regex = new RegExp(`(\-${andWord}\-)`, 'g');

  return param.replace(regex, ' & ').replace(/\-/g, ' ');
}

function getRootDomain(url: string) {
  let result = '';
  let segment;

  if (
    (segment = url.match(
      /^(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:\/\n\?\=]+)/im,
    ))
  ) {
    result = segment[1];

    if ((segment = result.match(/^[^\.]+\.(.+\..+)$/))) {
      const arrSegment = segment[0].split('.');

      if (arrSegment.length > 2 && arrSegment[0].length > 1) {
        result = segment[0];
      } else {
        result = segment[1];
      }
    }

    result = result.split('.')[0];
  }

  return result;
}

function fallbackCopyTextToClipboard(text: string) {
  const textArea = document.createElement('textarea');
  textArea.value = text;

  textArea.style.bottom = '0';
  textArea.style.left = '0';
  textArea.style.position = 'fixed';

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    document.execCommand('copy');
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Could not copy: ', err);
  }

  document.body.removeChild(textArea);
}

function copyTextToClipboard(text: string) {
  if (!navigator.clipboard) {
    fallbackCopyTextToClipboard(text);
    return;
  }
  navigator.clipboard.writeText(text).then(
    () => {},
    (err) => {
      // eslint-disable-next-line no-console
      console.error('Could not copy: ', err);
    },
  );
}

const asArray = <T = string>(
  value: T | Array<T> | undefined | null,
): Array<T> => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  return [value];
};

export {
  useNonInitialEffect,
  toNumber,
  nameToParam,
  paramToName,
  getRootDomain,
  copyTextToClipboard,
  asArray,
};
